import { Injectable, InjectionToken, Signal, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Auth,
  User,
  authState,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
} from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Role } from '@core/models/role.model';
import { UserProfileService } from '@core/services/user-profile.service';
import { VehiclesService } from '../../features/portal/cliente/services/vehicles.service';

export const AUTH_STATE$ = new InjectionToken<Observable<User | null>>('AUTH_STATE$', {
  providedIn: 'root',
  factory: () => authState(inject(Auth)),
});

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);
  private readonly authState$ = inject(AUTH_STATE$);
  private readonly userProfileService = inject(UserProfileService);
  private readonly vehiclesService = inject(VehiclesService);

  private readonly _currentUser = signal<User | null | undefined>(undefined);
  private readonly _ready = signal(false);

  /** `undefined` = loading, `null` = not authenticated, `User` = authenticated */
  readonly currentUser = this._currentUser.asReadonly();

  /** `true` once Firebase has resolved the initial auth state */
  readonly ready = this._ready.asReadonly();

  readonly isAuthenticated = computed(() => !!this._currentUser());

  /** Role assigned by the backend. `null` = no role yet or not authenticated */
  readonly currentUserRole: Signal<Role | null> = this.userProfileService.role;

  /** `true` once the backend profile has been fetched (or failed) */
  readonly profileReady: Signal<boolean> = this.userProfileService.ready;

  constructor() {
    this.authState$.pipe(takeUntilDestroyed()).subscribe((user) => {
      this._currentUser.set(user);
      this._ready.set(true);

      if (!environment.production) {
        console.log(
          user ? '[Auth] Usuario autenticado' : '[Auth] Firebase Auth conectado — sin sesión activa'
        );
      }
    });
  }

  /** Fetch profile from backend and populate role state. Call after sign-in. */
  loadUserProfile(): void {
    this.userProfileService.loadProfile().pipe(take(1)).subscribe();
  }

  signOut(): Observable<void> {
    return from(signOut(this.auth)).pipe(
      tap(() => {
        this._currentUser.set(null);
        this.userProfileService.reset();
        // Los guards de canal (Renovar, Facturación) leen este caché: no debe
        // sobrevivir al cierre de sesión y aplicarse al siguiente cliente.
        this.vehiclesService.reset();
      })
    );
  }

  /** Fresh JWT for authenticated API calls */
  getIdToken(): Observable<string | null> {
    // _currentUser() (signal) is set by the authState$ stream, which fires one async
    // tick *after* signInWithEmailLink resolves. The Firebase SDK's own auth.currentUser
    // property is updated synchronously at sign-in time, so we fall back to it to avoid
    // a 401 race condition when loadUserProfile() is called immediately after sign-in.
    const user = this._currentUser() ?? this.auth.currentUser;
    if (!user) return of(null);
    return from(user.getIdToken());
  }

  // --- Magic link (passwordless) ---

  sendSignInLink(email: string): Observable<void> {
    const payload = {
      email,
      redirectUrl: environment.magicLinkRedirectUrl,
      templateName: environment.magicLinkTemplateName,
      linkMode: environment.magicLinkMode,
    };
    return from(this.callMagicLinkFunction(payload)).pipe(
      tap(() => this.setPendingEmail(email)),
      map(() => void 0)
    );
  }

  completeSignIn(email: string, url: string): Observable<User> {
    return from(this.callSignInWithEmailLink(email, url)).pipe(
      tap(() => this.clearPendingEmail()),
      map((credential) => credential.user)
    );
  }

  isSignInLink(url: string): boolean {
    return isSignInWithEmailLink(this.auth, url);
  }

  // Protected wrappers around Firebase module functions — allows spying in unit tests
  // (ES module exports are non-writable and cannot be intercepted with spyOn directly)

  protected callMagicLinkFunction(payload: Record<string, unknown>): Promise<unknown> {
    const fn = httpsCallable(this.functions, environment.magicLinkFunctionName);
    return fn(payload);
  }

  protected callSignInWithEmailLink(email: string, url: string) {
    return signInWithEmailLink(this.auth, email, url);
  }

  hasPendingEmail(): boolean {
    return !!this.getPendingEmail();
  }

  getPendingEmail(): string | null {
    try {
      // NOTE: localStorage is intentional here — do NOT switch to sessionStorage.
      // The magic link flow works across tabs: the user requests the link in tab A,
      // then clicks it in their email client which opens tab B. sessionStorage is
      // tab-scoped, so tab B would never find the pending email and would show a
      // manual email confirmation form instead of signing in automatically.
      // localStorage persists across tabs (same origin), which is exactly what
      // this flow requires. The value is cleared immediately after a successful
      // sign-in via clearPendingEmail(), so it does not linger unnecessarily.
      return localStorage.getItem(this.PENDING_EMAIL_KEY);
    } catch {
      return null;
    }
  }

  private readonly PENDING_EMAIL_KEY = 'pendingSignInEmail';

  private setPendingEmail(email: string): void {
    try {
      localStorage.setItem(this.PENDING_EMAIL_KEY, email);
    } catch {
      // Storage unavailable (private mode, quota exceeded, etc.) — sign-in
      // will still proceed but the user may need to re-enter their email
      // on the complete-signin screen.
    }
  }

  clearPendingEmail(): void {
    try {
      localStorage.removeItem(this.PENDING_EMAIL_KEY);
    } catch {
      // Nothing to do if storage is unavailable
    }
  }
}
